/** Earn → Store: spend points on rewards, confirm before buying, and see what you've bought. */
import { useState } from 'react';
import { isTimedKind, storeEffect, type StoreItem, type StoreKind, type StoreOverview, type StoreRedemption } from '@platform/shared';
import type { GlyphName } from '../../components/NotificationIcon';
import { cx, Modal } from '../../components/ui';
import { api } from '../../lib/api';
import { errMsg, PillButton } from '../account/kit';
import { btn, EarnBadge, EarnRow, fmtPts, HeroPanel, Panel, type BadgeTone, type Msg } from './shared';

const ICON: Record<StoreKind, { glyph: GlyphName; tone: BadgeTone }> = {
  fee_discount: { glyph: 'percent', tone: 'accent' },
  spread_discount: { glyph: 'percent', tone: 'accent' },
  cashback_boost: { glyph: 'zap', tone: 'accent' },
  swap_free: { glyph: 'moon', tone: 'accent' },
  streak_freeze: { glyph: 'shield', tone: 'accent' },
  cash: { glyph: 'wallet', tone: 'done' },
};

const when = (iso: string) => new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
const day = (ymd: string) => new Date(`${ymd}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });

/** Where a purchase stands, in a few words. */
function stateOf(r: StoreRedemption): { text: string; tone: 'up' | 'muted' | 'accent' | 'warn' } {
  if (r.status === 'refunded') return { text: 'Refunded', tone: 'muted' };
  switch (r.kind) {
    case 'fee_discount':
    case 'spread_discount':
    case 'cashback_boost':
      return r.endsAt && Date.parse(r.endsAt) > Date.now() ? { text: `Running until ${when(r.endsAt)}`, tone: 'up' } : { text: 'Finished', tone: 'muted' };
    case 'swap_free':
      return r.usedAt ? { text: `Used on ${when(r.usedAt)}`, tone: 'muted' } : { text: 'Ready for the next swap', tone: 'accent' };
    case 'streak_freeze':
      return r.usedWeek ? { text: `Kept the week of ${day(r.usedWeek)}`, tone: 'muted' } : { text: 'Ready if you miss a week', tone: 'accent' };
    case 'cash':
      return { text: 'Added to your rewards to claim', tone: 'up' };
  }
}

const TONE = { up: 'bg-up-bg text-up', muted: 'bg-track text-muted', accent: 'bg-accent-bg text-accent-fg', warn: 'bg-warn-bg text-warn' };

function Confirm({ item, balance, onClose, onDone }: { item: StoreItem; balance: number; onClose: () => void; onDone: (m: Msg) => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const money = isTimedKind(item.kind) || item.kind === 'cash';
  return (
    <Modal open onClose={onClose} title={`Redeem ${item.name}?`} width={420}>
      <div className="flex flex-col gap-4 text-[14.5px]">
        <p className="m-0 text-muted">{storeEffect(item.kind, item.params)}.</p>
        <dl className="m-0 flex flex-col">
          {(
            [
              ['Cost', `${fmtPts(item.price)} points`],
              ['Points left after', `${fmtPts(balance - item.price)} points`],
              ['Starts', item.params.hours ? 'Now' : item.kind === 'swap_free' ? 'At the next swap' : item.kind === 'streak_freeze' ? 'When you miss a week' : 'Now'],
              ...(money ? [['Paid', 'As cashback you claim to your live account']] : []),
            ] as const
          ).map(([k, v]) => (
            <div key={k} className="flex justify-between gap-4 border-b border-line py-2.5 last:border-b-0">
              <dt className="text-muted">{k}</dt>
              <dd className="num m-0 text-right font-semibold">{v}</dd>
            </div>
          ))}
        </dl>
        <p className="m-0 text-[13px] text-muted">Points can't be given back once a reward has started.</p>
        {err && (
          <span role="alert" className="text-[13px] text-down">
            {err}
          </span>
        )}
        <div className="grid grid-cols-2 gap-2">
          <PillButton kind="secondary" size="sm" onClick={onClose}>
            Cancel
          </PillButton>
          <PillButton
            kind="accent"
            size="sm"
            busy={busy}
            onClick={async () => {
              setBusy(true);
              setErr(null);
              try {
                await api.post('/api/store/redeem', { itemId: item.id });
                onDone({ ok: true, text: `${item.name}: done.` });
                onClose();
              } catch (e) {
                setErr(errMsg(e));
              } finally {
                setBusy(false);
              }
            }}
          >
            Redeem
          </PillButton>
        </div>
      </div>
    </Modal>
  );
}

export function StoreTab({ data, onMsg }: { data: StoreOverview; onMsg: (m: Msg) => void }) {
  const [buying, setBuying] = useState<StoreItem | null>(null);
  return (
    <>
      <HeroPanel glow="85% 120%" className="!flex-row flex-wrap items-center !gap-5">
        <EarnBadge glyph="bag" tone="next" size="lg" />
        <div className="flex min-w-0 flex-col">
          <span className="text-[13px] text-muted">Ready to spend</span>
          <span className="num text-[32px] font-semibold tracking-tight md:text-[38px]">{fmtPts(data.balance)} pts</span>
          <span className="text-[14px] text-muted">
            {fmtPts(data.earned)} earned · {fmtPts(data.spent)} spent. Spending never lowers your rank.
          </span>
        </div>
      </HeroPanel>

      {data.items.length === 0 ? (
        <Panel>
          <p className="m-0 py-10 text-center text-[15px] text-muted">The store opens soon. Keep earning points: they'll be ready to spend.</p>
        </Panel>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {data.items.map((i) => {
            const ic = ICON[i.kind];
            const left = i.stock === null ? null : Math.max(0, i.stock - i.sold);
            return (
              <div key={i.id} className="flex min-w-0 flex-col gap-2 rounded-[22px] border border-line bg-card p-5">
                <div className="flex items-start justify-between gap-2">
                  <EarnBadge glyph={ic.glyph} tone={ic.tone} size="lg" />
                  {left !== null && <span className={cx('rounded-md px-2 py-0.5 text-[12px] font-medium', left <= 5 ? 'bg-warn-bg text-warn' : 'bg-track text-muted')}>{left} left</span>}
                </div>
                <b className="mt-1 text-[16px] font-semibold">{i.name}</b>
                <p className="m-0 text-[13.5px] text-muted">{i.description || storeEffect(i.kind, i.params)}</p>
                <div className="mt-auto flex items-center justify-between gap-2 pt-2">
                  <span className="num text-[15px] font-semibold">{fmtPts(i.price)} pts</span>
                  {i.blocked ? (
                    <span className="rounded-md bg-track px-2 py-1 text-right text-[12px] font-medium text-muted">{i.blocked}</span>
                  ) : (
                    <button type="button" className={btn('primary', 'sm')} onClick={() => setBuying(i)}>
                      Redeem
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Panel title="Redemptions" icon="clock">
        {data.redemptions.length === 0 ? (
          <p className="m-0 py-6 text-center text-[15px] text-muted">Rewards you redeem show up here.</p>
        ) : (
          <div className="flex flex-col gap-0.5">
            {data.redemptions.map((r) => {
              const st = stateOf(r);
              const ic = ICON[r.kind];
              return (
                <EarnRow
                  key={r.id}
                  badge={<EarnBadge glyph={ic.glyph} tone={st.tone === 'muted' ? 'muted' : ic.tone} />}
                  title={r.name}
                  body={`${fmtPts(r.points)} points · ${when(r.createdAt)}`}
                  className="items-center"
                >
                  <span className={cx('mt-1 self-start rounded-md px-2.5 py-1 text-[12.5px] font-medium', TONE[st.tone])}>{st.text}</span>
                </EarnRow>
              );
            })}
          </div>
        )}
      </Panel>
      {buying && <Confirm item={buying} balance={data.balance} onClose={() => setBuying(null)} onDone={onMsg} />}
    </>
  );
}
